<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div class="w-full px-6 py-8">
      <div class="space-y-8">
        <!-- Header Profile Card -->
        <div class="relative overflow-hidden bg-white shadow-2xl rounded-3xl border border-gray-100">
          <!-- Enhanced Background Pattern -->
          <div class="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-5"></div>
          <div class="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-10"></div>
          <div class="absolute bottom-0 left-0 -mb-8 -ml-8 w-32 h-32 bg-gradient-to-tr from-purple-400 to-pink-500 rounded-full opacity-10"></div>
          <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-200 to-purple-200 rounded-full opacity-5"></div>
          
          <div class="relative px-8 py-12 lg:px-12 lg:py-16">
            <div class="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
              <div class="flex-shrink-0">
                <div class="h-32 w-32 lg:h-36 lg:w-36 rounded-full bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 flex items-center justify-center shadow-2xl ring-8 ring-white/50 backdrop-blur-sm">
                  <svg class="h-16 w-16 lg:h-18 lg:w-18 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              <div class="flex-1 min-w-0 text-center lg:text-left">
                <h1 class="text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent leading-tight">
                  {{ user?.name || 'Usuario' }}
                </h1>
                <p class="text-xl lg:text-2xl text-gray-600 mt-2 font-medium">{{ user?.email }}</p>
                <div class="mt-6 flex flex-col lg:flex-row items-center lg:items-start space-y-3 lg:space-y-0 lg:space-x-6">
                  <span class="inline-flex items-center px-6 py-3 rounded-full text-base font-bold shadow-lg transform hover:scale-105 transition-all duration-200" :class="getRoleClass(user?.role)">
                    <svg class="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd" />
                    </svg>
                    {{ getRoleText(user?.role) }}
                  </span>
                  <div class="flex items-center text-base text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                    <svg class="w-5 h-5 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Miembro desde {{ formatDate(user?.createdAt) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Block Status Banner -->
        <div v-if="user?.isBlocked" class="bg-red-50 border border-red-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div>
              <p class="text-base font-bold text-red-800">Usuario bloqueado</p>
              <p v-if="user?.blockedReason" class="text-sm text-red-700 mt-0.5">Motivo: {{ user.blockedReason }}</p>
              <p v-if="user?.blockedUntil" class="text-sm text-red-600 mt-0.5 font-medium">{{ timeUntilUnblock(user.blockedUntil) }} · hasta {{ new Date(user.blockedUntil).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</p>
              <p v-else class="text-sm text-red-600 mt-0.5 font-medium">Bloqueo indefinido</p>
            </div>
          </div>
          <button @click="showUnblockModal = true" :disabled="blockLoading" class="flex-shrink-0 inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            {{ blockLoading ? 'Desbloqueando...' : 'Desbloquear usuario' }}
          </button>
        </div>
        <div v-else-if="user" class="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p class="text-sm font-semibold text-green-800">Usuario activo — Sin restricciones de acceso</p>
          </div>
          <button @click="openBlockModal" class="flex-shrink-0 inline-flex items-center px-4 py-2 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            Bloquear usuario
          </button>
        </div>

        <!-- User Information Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          <!-- Name Card -->
          <div class="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-500 mb-2">Nombre completo</p>
              <p class="text-xl font-bold text-gray-900">{{ user?.name || 'No especificado' }}</p>
            </div>
          </div>

          <!-- Email Card -->
          <div class="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-green-200 transform hover:-translate-y-1">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-500 mb-2">Correo electrónico</p>
              <p class="text-xl font-bold text-gray-900 break-all">{{ user?.email || 'No especificado' }}</p>
            </div>
          </div>

          <!-- Role Card -->
          <div class="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-purple-200 transform hover:-translate-y-1">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd" />
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-500 mb-2">Rol del usuario</p>
              <p class="text-xl font-bold text-gray-900">{{ getRoleText(user?.role) }}</p>
            </div>
          </div>

          <!-- Pets Count Card -->
          <div class="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-orange-200 transform hover:-translate-y-1">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-500 mb-2">Total de mascotas</p>
              <p class="text-xl font-bold text-gray-900">{{ userPets?.length || 0 }} mascotas</p>
            </div>
          </div>
        </div>

        <!-- Subscription & Streak Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          <!-- Subscription Card -->
          <div class="md:col-span-2 bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-yellow-200 transform hover:-translate-y-1">
            <div class="flex items-start justify-between mb-6">
              <div class="flex items-center gap-4">
                <div class="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Suscripción</p>
                  <div v-if="subscription" class="flex items-center gap-2 mt-1">
                    <span
                      class="px-3 py-1 rounded-full text-sm font-bold"
                      :style="subscription.plan.type === 'premium' ? 'background:#fef3c7;color:#92400e' : 'background:#f3f4f6;color:#374151'"
                    >{{ subscription.plan.type === 'premium' ? 'Premium' : 'Free' }}</span>
                    <span class="text-base font-bold text-gray-900">{{ subscription.plan.name }}</span>
                  </div>
                  <p v-else class="text-base font-bold text-gray-900 mt-1">Plan Free</p>
                </div>
              </div>
              <button
                @click="openGrantPremiumModal"
                style="background-color:#1d4ed8;color:#fff"
                class="px-4 py-2 rounded-xl text-sm font-semibold shadow-md hover:opacity-90 transition-opacity flex-shrink-0"
              >Otorgar Premium</button>
            </div>
            <div v-if="subscriptionLoading" class="text-sm text-gray-400">Cargando...</div>
            <div v-else-if="subscription" class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div class="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                <p class="text-2xl font-bold text-blue-700">{{ subscription.plan.likesPerDay === -1 ? '∞' : subscription.plan.likesPerDay }}</p>
                <p class="text-xs font-medium text-blue-600 mt-1">Likes/día</p>
              </div>
              <div class="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                <p class="text-2xl font-bold text-purple-700">{{ subscription.plan.superLikesPerDay }}</p>
                <p class="text-xs font-medium text-purple-600 mt-1">Super-likes/día</p>
              </div>
              <div class="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                <p class="text-2xl font-bold text-green-700">{{ subscription.plan.maxPets }}</p>
                <p class="text-xs font-medium text-green-600 mt-1">Máx. mascotas</p>
              </div>
              <div class="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
                <p class="text-sm font-bold text-orange-700">{{ subscription.subscription?.expiresAt ? formatDate(subscription.subscription.expiresAt) : 'Sin límite' }}</p>
                <p class="text-xs font-medium text-orange-600 mt-1">Vencimiento</p>
              </div>
            </div>
            <div v-else class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div class="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                <p class="text-2xl font-bold text-blue-700">10</p>
                <p class="text-xs font-medium text-blue-600 mt-1">Likes/día</p>
              </div>
              <div class="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                <p class="text-2xl font-bold text-purple-700">0</p>
                <p class="text-xs font-medium text-purple-600 mt-1">Super-likes/día</p>
              </div>
              <div class="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                <p class="text-2xl font-bold text-green-700">1</p>
                <p class="text-xs font-medium text-green-600 mt-1">Máx. mascotas</p>
              </div>
              <div class="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
                <p class="text-sm font-bold text-gray-600">Sin límite</p>
                <p class="text-xs font-medium text-gray-500 mt-1">Vencimiento</p>
              </div>
            </div>
          </div>

          <!-- Streak Card -->
          <div class="md:col-span-2 bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-indigo-200 transform hover:-translate-y-1">
            <div class="flex items-center gap-4 mb-6">
              <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Racha de login</p>
                <p class="text-base font-bold text-gray-900 mt-0.5" v-if="streak?.lastLoginDate">Último: {{ formatDate(streak.lastLoginDate) }}</p>
                <p class="text-base font-bold text-gray-400 mt-0.5" v-else>Sin actividad</p>
              </div>
            </div>
            <div v-if="subscriptionLoading" class="text-sm text-gray-400">Cargando...</div>
            <div v-else class="grid grid-cols-3 gap-4">
              <div class="bg-indigo-50 rounded-xl p-4 text-center border border-indigo-100">
                <p class="text-3xl font-bold text-indigo-700">{{ streak?.currentStreak || 0 }}</p>
                <p class="text-xs font-medium text-indigo-600 mt-1">Racha actual</p>
              </div>
              <div class="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                <p class="text-3xl font-bold text-purple-700">{{ streak?.longestStreak || 0 }}</p>
                <p class="text-xs font-medium text-purple-600 mt-1">Récord</p>
              </div>
              <div class="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                <p class="text-3xl font-bold text-green-700">{{ streak?.totalLogins || 0 }}</p>
                <p class="text-xs font-medium text-green-600 mt-1">Logins totales</p>
              </div>
            </div>
          </div>
        </div>

        <!-- User's Pets Section -->
        <div class="bg-white shadow-2xl rounded-3xl overflow-hidden border border-gray-100">
          <div class="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 px-8 py-6">
            <h3 class="text-2xl lg:text-3xl font-bold text-white flex items-center">
              <svg class="w-8 h-8 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Mascotas de {{ user?.name }}
            </h3>
          </div>
          
          <div class="p-8">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-16">
              <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
              <p class="mt-6 text-xl text-gray-600">Cargando mascotas...</p>
            </div>

            <!-- Pets Grid -->
            <div v-else-if="userPets && userPets.length > 0" class="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              <div v-for="pet in userPets" :key="pet.id" class="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-indigo-300 transform hover:-translate-y-2">
                <div class="flex flex-col items-center text-center mb-6">
                  <div class="relative mb-4">
                    <img 
                      :src="pet.image || '/placeholder-pet.jpg'" 
                      :alt="pet.name"
                      class="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-xl"
                    >
                    <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center shadow-lg">
                      <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <h4 class="text-xl font-bold text-gray-900 mb-1">{{ pet.name }}</h4>
                  <p class="text-lg text-indigo-600 font-semibold mb-1">{{ pet.age }} años</p>
                  <p class="text-sm text-gray-500">{{ pet.breed?.name }} • {{ pet.breed?.animalType?.name }}</p>
                </div>
                
                <div class="space-y-4">
                  <div class="bg-white rounded-xl p-4 shadow-sm">
                    <p class="text-sm text-gray-700 line-clamp-3">{{ pet.description || 'Sin descripción disponible' }}</p>
                  </div>
                  
                  <div class="grid grid-cols-2 gap-4">
                    <div class="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                      <div class="flex items-center justify-center mb-2">
                        <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l3-1m-3 1l-3-1" />
                        </svg>
                      </div>
                      <p class="text-xs font-semibold text-blue-600 mb-1">Peso</p>
                      <p class="text-lg font-bold text-blue-800">{{ pet.weight || 'N/A' }} kg</p>
                    </div>
                    
                    <div class="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                      <div class="flex items-center justify-center mb-2">
                        <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                        </svg>
                      </div>
                      <p class="text-xs font-semibold text-purple-600 mb-1">Color</p>
                      <p class="text-lg font-bold text-purple-800">{{ pet.color || 'N/A' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-16">
              <div class="mx-auto w-32 h-32 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mb-6 shadow-lg">
                <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3">No hay mascotas registradas</h3>
              <p class="text-lg text-gray-500 max-w-lg mx-auto">Este usuario aún no ha registrado ninguna mascota en el sistema.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Otorgar Premium -->
  <div v-if="showGrantModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <h3 class="text-lg font-semibold mb-4">Otorgar Premium a {{ user?.name }}</h3>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Plan</label>
          <select v-model="grantForm.planId" class="input w-full">
            <option v-for="p in premiumPlans" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Días a otorgar</label>
          <input v-model="grantForm.days" type="number" min="1" class="input w-full" />
        </div>
      </div>
      <div class="flex justify-end gap-3 mt-6">
        <button @click="showGrantModal = false" style="background-color:#475569;color:#fff" class="px-4 py-2 rounded-lg text-sm font-medium">Cancelar</button>
        <button @click="submitGrantPremium" :disabled="grantLoading" style="background-color:#1d4ed8;color:#fff" class="px-4 py-2 rounded-lg text-sm font-medium">
          {{ grantLoading ? 'Procesando...' : 'Otorgar' }}
        </button>
      </div>
    </div>
  </div>
  <div v-if="showBlockModal" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-60" @click="showBlockModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md z-10">
        <div class="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4 rounded-t-xl">
          <h3 class="text-lg font-bold text-white">Bloquear acceso al app</h3>
          <p class="text-orange-100 text-sm mt-0.5">{{ user?.name }} no podrá iniciar sesión hasta ser desbloqueado</p>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Duración del bloqueo</label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="opt in blockDurationOptions"
                :key="opt.value"
                @click="blockDuration = opt.value"
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border-2 transition-all"
                :class="blockDuration === opt.value ? 'border-red-500 bg-red-50 text-red-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Motivo del bloqueo (opcional)</label>
            <textarea
              v-model="blockReason"
              rows="3"
              placeholder="Ej: Comportamiento inapropiado, acoso a otros usuarios..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            ></textarea>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-xl">
          <button @click="executeBlock" :disabled="blockLoading" class="btn btn-danger">{{ blockLoading ? 'Bloqueando...' : 'Confirmar bloqueo' }}</button>
          <button @click="showBlockModal = false" class="btn btn-secondary">Cancelar</button>
        </div>
      </div>
    </div>
  </div>
  <!-- Unblock Confirm Modal -->
  <div v-if="showUnblockModal" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-60" @click="showUnblockModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md z-10">
        <div class="px-6 pt-6 pb-4">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-100">
              <svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-medium text-gray-900">Desbloquear usuario</h3>
              <p class="text-sm text-gray-500 mt-1">¿Confirmas que deseas desbloquear a <strong>{{ user?.name }}</strong>? El usuario podrá volver a iniciar sesión inmediatamente.</p>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-xl">
          <button @click="unblockUser" :disabled="blockLoading" class="btn btn-primary">{{ blockLoading ? 'Desbloqueando...' : 'Confirmar desbloqueo' }}</button>
          <button @click="showUnblockModal = false" class="btn btn-secondary">Cancelar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useUsersStore } from '../stores/users'
import { petService } from '../services/petService'
import { useToast } from 'vue-toastification'
import apiService from '../services/api'
import { subscriptionService } from '../services/subscriptionService'

export default {
  name: 'UserProfile',
  setup() {
    const route = useRoute()
    const usersStore = useUsersStore()
    const toast = useToast()
    const user = ref(null)
    const userPets = ref([])
    const loading = ref(false)
    const showBlockModal = ref(false)
    const showUnblockModal = ref(false)
    const blockLoading = ref(false)
    const blockReason = ref('')
    const blockDuration = ref('indefinite')

    const blockDurationOptions = [
      { label: '1 día', value: '1d' },
      { label: '3 días', value: '3d' },
      { label: '7 días', value: '7d' },
      { label: '30 días', value: '30d' },
      { label: 'Indefinido', value: 'indefinite' }
    ]

    const subscription = ref(null)
    const streak = ref(null)
    const subscriptionLoading = ref(false)
    const showGrantModal = ref(false)
    const grantLoading = ref(false)
    const premiumPlans = ref([])
    const grantForm = ref({ planId: null, days: 30 })

    const openGrantPremiumModal = async () => {
      const res = await subscriptionService.getPlans()
      premiumPlans.value = (res.data || []).filter((p) => p.type === 'premium')
      if (premiumPlans.value.length > 0) grantForm.value.planId = premiumPlans.value[0].id
      showGrantModal.value = true
    }

    const submitGrantPremium = async () => {
      grantLoading.value = true
      try {
        await subscriptionService.grantPremium(
          user.value.id,
          grantForm.value.planId,
          grantForm.value.days
        )
        toast.success('Premium otorgado correctamente')
        showGrantModal.value = false
        await loadSubscriptionData(user.value.id)
      } catch {
        toast.error('Error al otorgar premium')
      } finally {
        grantLoading.value = false
      }
    }

    const loadSubscriptionData = async (userId) => {
      subscriptionLoading.value = true
      try {
        const [subRes, streakRes] = await Promise.all([
          apiService.api.get(`/subscriptions/my`).catch(() => ({ data: { data: null } })),
          apiService.api.get(`/subscriptions/streak`).catch(() => ({ data: { data: null } })),
        ])
        subscription.value = subRes.data?.data ?? null
        streak.value = streakRes.data?.data ?? null
      } finally {
        subscriptionLoading.value = false
      }
    }

    const getBlockedUntil = () => {
      if (blockDuration.value === 'indefinite') return undefined
      const days = parseInt(blockDuration.value)
      const date = new Date()
      date.setDate(date.getDate() + days)
      return date.toISOString()
    }

    const timeUntilUnblock = (blockedUntil) => {
      if (!blockedUntil) return null
      const now = new Date()
      const until = new Date(blockedUntil)
      const diff = until - now
      if (diff <= 0) return 'Expirado'
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      if (days > 0) return `${days} día${days !== 1 ? 's' : ''} y ${hours}h restantes`
      return `${hours} hora${hours !== 1 ? 's' : ''} restantes`
    }

    const openBlockModal = () => {
      blockReason.value = ''
      blockDuration.value = 'indefinite'
      showBlockModal.value = true
    }

    const executeBlock = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${user.value.id}/block`, {
          action: 'block',
          reason: blockReason.value || undefined,
          blockedUntil: getBlockedUntil()
        })
        user.value.isBlocked = true
        user.value.blockedReason = blockReason.value || null
        user.value.blockedUntil = getBlockedUntil() || null
        toast.success('Usuario bloqueado')
        showBlockModal.value = false
      } catch {
        toast.error('Error al bloquear al usuario')
      } finally {
        blockLoading.value = false
      }
    }

    const unblockUser = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${user.value.id}/block`, { action: 'unblock' })
        user.value.isBlocked = false
        user.value.blockedReason = null
        user.value.blockedUntil = null
        toast.success('Usuario desbloqueado')
        showUnblockModal.value = false
      } catch {
        toast.error('Error al desbloquear al usuario')
      } finally {
        blockLoading.value = false
      }
    }

    const getRoleClass = (role) => {
      switch (role) {
        case 'admin':
          return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-200'
        case 'user':
          return 'bg-gradient-to-r from-green-500 to-green-600 text-white border-green-200'
        default:
          return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white border-gray-200'
      }
    }

    const getRoleText = (role) => {
      switch (role) {
        case 'admin':
          return 'Administrador'
        case 'user':
          return 'Usuario'
        default:
          return 'Desconocido'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'No disponible'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const loadUserData = async () => {
      try {
        loading.value = true
        const userId = route.params.id
        
        if (!userId) {
          console.error('No user ID provided')
          return
        }
        
        // Load user data
        user.value = await usersStore.getUserById(parseInt(userId))
        
        // Load user's pets
        const petsResponse = await petService.getPetsByUser(parseInt(userId))
        console.log('Pets response:', petsResponse)
        userPets.value = petsResponse.data || petsResponse || []
        await loadSubscriptionData(parseInt(userId))
      } catch (error) {
        console.error('Error loading user data:', error)
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadUserData()
    })

    return {
      user,
      userPets,
      loading,
      showBlockModal,
      showUnblockModal,
      blockLoading,
      blockReason,
      blockDuration,
      blockDurationOptions,
      timeUntilUnblock,
      openBlockModal,
      executeBlock,
      unblockUser,
      getRoleClass,
      getRoleText,
      formatDate,
      subscription,
      streak,
      subscriptionLoading,
      showGrantModal,
      grantLoading,
      premiumPlans,
      grantForm,
      openGrantPremiumModal,
      submitGrantPremium,
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>